import React, { useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Save, Trash2, Building2, Loader2, Pencil, Square, Move, X, Sparkles } from 'lucide-react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { runIsometricPipeline } from '../../lib/aiRender';
import { useFloorPlans } from '../../lib/hooks/useFloorPlans';
import { useApartments } from '../../lib/hooks/useApartments';
import { useBuildingConfig } from '../../lib/hooks/useBuildingConfig';

interface Point {
  x: number;
  y: number;
}

interface ApartmentShape {
  id: string;
  apartmentId: string | null;
  points: Point[];
}

const FloorPlansEditor = () => {
  const { config: buildingConfig, getAvailableFloors } = useBuildingConfig();
  const [selectedEntrance, setSelectedEntrance] = useState('1');
  const [selectedFloor, setSelectedFloor] = useState(1);
  const [shapes, setShapes] = useState<ApartmentShape[]>([]);
  const [isoShapes, setIsoShapes] = useState<ApartmentShape[]>([]);
  const [editView, setEditView] = useState<'2d' | '3d'>('2d');
  const [hoverPos, setHoverPos] = useState<Point | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState<Point[]>([]);
  const [drawingMode, setDrawingMode] = useState<'draw' | 'select'>('select');
  const [selectedShape, setSelectedShape] = useState<string | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [showApartmentForm, setShowApartmentForm] = useState(false);
  const [tempShape, setTempShape] = useState<ApartmentShape | null>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  
  const imageRef = useRef<HTMLImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    floorPlans,
    loading: floorPlansLoading,
    error: floorPlansError,
    uploadFloorPlan,
    updateFloorPlan,
    deleteFloorPlan
  } = useFloorPlans(selectedEntrance, selectedFloor);

  const {
    apartments,
    loading: apartmentsLoading,
    error: apartmentsError
  } = useApartments(selectedEntrance, selectedFloor.toString());

  useEffect(() => {
    if (floorPlans.length > 0) {
      setShapes(floorPlans[0].apartments || []);
      setIsoShapes((floorPlans[0].isometricApartments as ApartmentShape[]) || []);
    }
  }, [floorPlans]);

  // The active outline set follows the current editor view (2D plan / 3D render).
  const activeShapes = editView === '2d' ? shapes : isoShapes;
  const setActiveShapes = (updater: (prev: ApartmentShape[]) => ApartmentShape[]) => {
    if (editView === '2d') setShapes(updater);
    else setIsoShapes(updater);
  };

  // Reset transient drawing state when switching between 2D and 3D views.
  const switchEditView = (view: '2d' | '3d') => {
    setEditView(view);
    setCurrentPoints([]);
    setSelectedShape(null);
    setTempShape(null);
    setShowApartmentForm(false);
    setHoverPos(null);
    setImageDimensions({ width: 0, height: 0 });
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (imageRef.current) {
        const { naturalWidth, naturalHeight } = imageRef.current;
        setImageDimensions({ width: naturalWidth, height: naturalHeight });
      }
    };

    const image = imageRef.current;
    if (image) {
      if (image.complete) {
        updateDimensions();
      } else {
        image.addEventListener('load', updateDimensions);
      }
    }

    return () => {
      if (image) {
        image.removeEventListener('load', updateDimensions);
      }
    };
  }, [floorPlans, editView]);

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      try {
        setIsSaving(true);
        await uploadFloorPlan(file, selectedEntrance, selectedFloor);
      } catch (error) {
        console.error('Error uploading floor plan:', error);
        alert('Грешка при качване на етажния план');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    multiple: false
  });

  const getMousePosition = (event: React.MouseEvent): Point | null => {
    if (!svgRef.current || !imageRef.current) return null;
    
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    
    // Get click coordinates relative to SVG
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Convert to percentage coordinates
    return {
      x: (x / rect.width) * imageDimensions.width,
      y: (y / rect.height) * imageDimensions.height
    };
  };

  // Snaps a raw point to: (1) a nearby existing vertex (own or other shapes),
  // else (2) — on the 2D plan only — a 45°/90° angle from the previous point.
  // The isometric render is in perspective, so no angle snapping applies there:
  // points connect freely at any angle.
  const snapPoint = (x: number, y: number): Point => {
    const vsnap = imageDimensions.width * 0.015;
    let best: { p: Point; d: number } | null = null;
    const others = [...activeShapes.flatMap((s) => s.points), ...currentPoints.slice(0, -1)];
    others.forEach((p) => {
      const d = Math.hypot(p.x - x, p.y - y);
      if (d < vsnap && (!best || d < best.d)) best = { p, d };
    });
    if (best) return { x: best.p.x, y: best.p.y };

    if (editView === '2d' && currentPoints.length > 0) {
      const prev = currentPoints[currentPoints.length - 1];
      const dx = x - prev.x;
      const dy = y - prev.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 1) {
        const ang = Math.atan2(dy, dx);
        const step = Math.PI / 4;
        const snapped = Math.round(ang / step) * step;
        let diff = ang - snapped;
        diff = Math.atan2(Math.sin(diff), Math.cos(diff));
        if (Math.abs(diff) < (14 * Math.PI) / 180) {
          return { x: prev.x + Math.cos(snapped) * dist, y: prev.y + Math.sin(snapped) * dist };
        }
      }
    }
    return { x, y };
  };

  const handleMouseClick = (event: React.MouseEvent) => {
    if (drawingMode !== 'draw') return;

    const raw = getMousePosition(event);
    if (!raw) return;
    const point = snapPoint(raw.x, raw.y);

    // Check if we're closing the polygon
    if (currentPoints.length > 2) {
      const firstPoint = currentPoints[0];
      const distance = Math.hypot(point.x - firstPoint.x, point.y - firstPoint.y);
      if (distance < imageDimensions.width * 0.01) {
        const newShape: ApartmentShape = {
          id: Date.now().toString(),
          apartmentId: null,
          points: [...currentPoints]
        };
        setTempShape(newShape);
        setShowApartmentForm(true);
        setCurrentPoints([]);
        setHoverPos(null);
        return;
      }
    }

    setCurrentPoints(prev => [...prev, point]);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (drawingMode !== 'draw') return;
    setHoveredPoint(null);

    const raw = getMousePosition(event);
    if (!raw) return;
    const point = snapPoint(raw.x, raw.y);
    setHoverPos(point);

    // Check if we're hovering near the starting point
    if (currentPoints.length > 2) {
      const startPoint = currentPoints[0];
      const distance = Math.hypot(point.x - startPoint.x, point.y - startPoint.y);
      if (distance < imageDimensions.width * 0.01) {
        setHoveredPoint(0);
      }
    }
  };

  const undoLastPoint = () => setCurrentPoints((prev) => prev.slice(0, -1));

  const cancelDrawing = () => {
    setCurrentPoints([]);
    setIsDrawing(false);
    setTempShape(null);
    setShowApartmentForm(false);
    setHoverPos(null);
  };

  const pointsToPath = (points: Point[] | undefined): string => {
    if (!points || points.length === 0) return '';
    return `M ${points[0].x} ${points[0].y} ${
      points.slice(1).map(point => `L ${point.x} ${point.y}`).join(' ')
    }${points.length > 2 ? ' Z' : ''}`;
  };

  const handleShapeClick = (event: React.MouseEvent, shapeId: string) => {
    if (drawingMode === 'select') {
      event.stopPropagation();
      setSelectedShape(shapeId);
    }
  };

  const handleSave = async () => {
    if (floorPlans.length > 0) {
      const currentPlan = floorPlans[0];
      try {
        setIsSaving(true);
        await updateFloorPlan(currentPlan.id, { apartments: shapes, isometricApartments: isoShapes });
        alert('Етажният план е запазен успешно!');
      } catch (error) {
        console.error('Error saving floor plan:', error);
        alert('Грешка при запазване на етажния план');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const [isGeneratingIso, setIsGeneratingIso] = useState(false);
  const [isoStage, setIsoStage] = useState('');

  // AI isometric render of the current floor plan (edge function -> Storage).
  const handleGenerateIsometric = async () => {
    if (floorPlans.length === 0) return;
    const currentPlan = floorPlans[0];
    if (
      currentPlan.isometricUrl &&
      !window.confirm(
        'Вече има 3D изглед за този етаж. Прегенерирането ще го замени и ще ИЗЧИСТИ очертаните върху него апартаменти (те няма да съвпадат с новото изображение). Продължавам?'
      )
    )
      return;
    setIsGeneratingIso(true);
    try {
      const { blob, model } = await runIsometricPipeline(currentPlan.imageUrl, 'floor', setIsoStage);
      const r = storageRef(storage, `floorPlans/iso_floor${selectedFloor}_${Date.now()}.png`);
      await uploadBytes(r, blob, { contentType: blob.type || 'image/png' });
      const url = await getDownloadURL(r);
      // Outlines were traced in the previous image's pixel space — they can
      // never match a newly generated composition, so clear them.
      await updateFloorPlan(currentPlan.id, { isometricUrl: url, isometricApartments: [] });
      setIsoShapes([]);
      setSelectedShape(null);
      setCurrentPoints([]);
      alert(
        model === 'pro'
          ? '3D изгледът е генериран успешно (Pro модел).'
          : '3D изгледът е генериран с резервния Flash модел (по-ниска точност) — пробвайте „Прегенерирай" за Pro качество.'
      );
    } catch (error: any) {
      console.error('Error generating isometric render:', error);
      alert(error.message || 'Грешка при генериране на 3D изгледа');
    } finally {
      setIsGeneratingIso(false);
      setIsoStage('');
    }
  };

  const handleDelete = async () => {
    if (floorPlans.length > 0) {
      const currentPlan = floorPlans[0];
      if (window.confirm('Сигурни ли сте, че искате да изтриете този етажен план?')) {
        try {
          setIsSaving(true);
          await deleteFloorPlan(currentPlan.id, currentPlan.imageUrl);
          setShapes([]);
          alert('Етажният план е изтрит успешно!');
        } catch (error) {
          console.error('Error deleting floor plan:', error);
          if (error instanceof Error) {
            alert(error.message);
          } else {
            alert('Възникна неочаквана грешка при изтриване на етажния план');
          }
        } finally {
          setIsSaving(false);
        }
      }
    }
  };

  const handleDeleteShape = (shapeId: string) => {
    setActiveShapes(prev => prev.filter(shape => shape.id !== shapeId));
    setSelectedShape(null);
  };

  const handleAssignApartment = (shapeId: string, apartmentId: string | null) => {
    if (tempShape && showApartmentForm) {
      // Adding new shape
      setActiveShapes(prev => [...prev, { ...tempShape, apartmentId }]);
      setTempShape(null);
      setShowApartmentForm(false);
    } else {
      // Updating existing shape
      setActiveShapes(prev => prev.map(shape =>
        shape.id === shapeId ? { ...shape, apartmentId } : shape
      ));
    }
  };

  const getApartmentById = (apartmentId: string | null) => {
    if (!apartmentId) return null;
    return apartments.find(apt => apt.id === apartmentId);
  };

  if (floorPlansLoading || apartmentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (floorPlansError || apartmentsError) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{floorPlansError || apartmentsError}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Building2 className="w-8 h-8 text-primary mr-3" />
          <h1 className="text-2xl font-bold">Етажни планове</h1>
        </div>
        <div className="flex space-x-4">
          <select
            value={selectedEntrance}
            onChange={(e) => setSelectedEntrance(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white shadow-sm hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
          >
            {buildingConfig.entrances.map((entrance) => (
              <option key={entrance.id} value={entrance.id}>
                {entrance.name}
              </option>
            ))}
          </select>
          <select
            value={selectedFloor}
            onChange={(e) => setSelectedFloor(Number(e.target.value))}
            className="px-4 py-2 border rounded-lg bg-white shadow-sm hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
          >
            {getAvailableFloors(selectedEntrance).map((floor) => (
              <option key={floor} value={floor}>Етаж {floor}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {floorPlans.length > 0 && (
            <div className="flex justify-center mb-4">
              <div className="inline-flex rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                <button
                  onClick={() => switchEditView('2d')}
                  className={`px-6 py-2 text-sm font-medium transition-colors ${
                    editView === '2d' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  2D план
                </button>
                <button
                  onClick={() => switchEditView('3d')}
                  className={`px-6 py-2 text-sm font-medium transition-colors ${
                    editView === '3d' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  3D изглед
                </button>
              </div>
            </div>
          )}

          {floorPlans.length > 0 && editView === '3d' && !floorPlans[0].isometricUrl ? (
            <div className="border rounded-lg bg-white shadow-lg p-12 text-center">
              <Sparkles className="w-12 h-12 text-secondary mx-auto mb-4" />
              <p className="text-gray-600 mb-6">
                Този етаж още няма 3D изглед. Генерирайте го с AI от 2D плана, след което можете да
                очертаете апартаментите върху него.
              </p>
              <button
                onClick={handleGenerateIsometric}
                disabled={isGeneratingIso}
                className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-secondary to-secondary-dark text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isGeneratingIso ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    <span>{isoStage || 'Генерирам...'}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    <span>Генерирай 3D изглед (AI)</span>
                  </>
                )}
              </button>
            </div>
          ) : floorPlans.length > 0 ? (
            <>
            {/* Toolbar above the plan: nothing may overlap the sketch, or the
                area under a button cannot be traced. It is always rendered so
                the plan does not jump when the drawing actions appear. */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 min-h-[42px]">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setDrawingMode('draw');
                    setSelectedShape(null);
                    setCurrentPoints([]);
                  }}
                  className={`p-2 rounded-lg border ${
                    drawingMode === 'draw'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                  title="Начертай апартамент"
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setDrawingMode('select');
                    setSelectedShape(null);
                    setCurrentPoints([]);
                  }}
                  className={`p-2 rounded-lg border ${
                    drawingMode === 'select'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                  title="Избери апартамент"
                >
                  <Move className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                {drawingMode === 'draw' && currentPoints.length > 0 && (
                  <>
                    <button
                      onClick={undoLastPoint}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="Върни последната точка"
                    >
                      Върни точка
                    </button>
                    <button
                      onClick={cancelDrawing}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Отказ
                    </button>
                  </>
                )}

                {editView === '3d' && drawingMode !== 'draw' && (
                  <button
                    onClick={handleGenerateIsometric}
                    disabled={isGeneratingIso}
                    title="Прегенерирай 3D изгледа с AI"
                    className="flex items-center px-3 py-2 bg-gradient-to-r from-secondary to-secondary-dark text-white rounded-lg shadow hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
                  >
                    {isGeneratingIso ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {isGeneratingIso ? (isoStage || 'Генерирам...') : 'Прегенерирай'}
                  </button>
                )}
              </div>
            </div>

            <div className="relative border rounded-lg overflow-hidden bg-white shadow-lg">
              <div className="relative" style={{ fontSize: 0 }}>
                <img
                  ref={imageRef}
                  src={editView === '2d' ? floorPlans[0].imageUrl : floorPlans[0].isometricUrl}
                  alt={`Floor plan ${selectedFloor}`}
                  className="w-full h-auto"
                />
                <svg
                  ref={svgRef}
                  className="absolute inset-0 w-full h-full"
                  viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                  preserveAspectRatio="xMidYMid meet"
                  onClick={handleMouseClick}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setHoverPos(null)}
                >
                  {/* Existing shapes */}
                  {activeShapes.map((shape) => (
                    <path
                      key={shape.id}
                      d={pointsToPath(shape.points)}
                      fill={selectedShape === shape.id ? 'rgba(44, 62, 80, 0.4)' : 'rgba(44, 62, 80, 0.2)'}
                      stroke="#2C3E50"
                      strokeWidth={imageDimensions.width * 0.002}
                      className="cursor-pointer hover:fill-opacity-40 transition-colors"
                      onClick={(e) => handleShapeClick(e, shape.id)}
                    />
                  ))}

                  {/* Rubber-band preview to the snapped cursor position */}
                  {drawingMode === 'draw' && hoverPos && currentPoints.length > 0 && (
                    <line
                      x1={currentPoints[currentPoints.length - 1].x}
                      y1={currentPoints[currentPoints.length - 1].y}
                      x2={hoverPos.x}
                      y2={hoverPos.y}
                      stroke="#2C3E50"
                      strokeWidth={imageDimensions.width * 0.002}
                      strokeDasharray={`${imageDimensions.width * 0.006},${imageDimensions.width * 0.006}`}
                      strokeOpacity={0.55}
                    />
                  )}
                  {drawingMode === 'draw' && hoverPos && (
                    <circle
                      cx={hoverPos.x}
                      cy={hoverPos.y}
                      r={imageDimensions.width * 0.006}
                      fill="#2C3E50"
                      fillOpacity={0.5}
                    />
                  )}

                  {/* Current drawing */}
                  {currentPoints.length > 0 && (
                    <>
                      <path
                        d={pointsToPath(currentPoints)}
                        fill="none"
                        stroke="#2C3E50"
                        strokeWidth={imageDimensions.width * 0.002}
                        strokeDasharray="4"
                      />
                      {currentPoints.map((point, index) => (
                        <circle
                          key={index}
                          cx={point.x}
                          cy={point.y}
                          r={imageDimensions.width * 0.005}
                          fill={index === 0 ? '#2C3E50' : '#fff'}
                          stroke="#2C3E50"
                          strokeWidth={imageDimensions.width * 0.001}
                          className={index === 0 && hoveredPoint === 0 ? 'animate-pulse' : ''}
                        />
                      ))}
                    </>
                  )}
                </svg>
              </div>
            </div>
            </>
          ) : (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary hover:bg-primary/5'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-lg text-gray-600">
                {isDragActive
                  ? 'Пуснете файла тук...'
                  : 'Плъзнете етажен план или кликнете, за да изберете файл'}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Поддържани формати: PNG, JPG, JPEG
              </p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-6">Апартаменти на етажа</h2>
            {drawingMode === 'draw' ? (
              <div className="text-sm text-gray-600 space-y-2">
                <p>Кликнете върху плана, за да добавите точки за апартамента.</p>
                <p>Кликнете върху първата точка, за да затворите формата.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apartments.map((apartment) => {
                  const isAssigned = activeShapes.some(shape => shape.apartmentId === apartment.id);
                  const isSelected = selectedShape && activeShapes.find(shape => shape.id === selectedShape)?.apartmentId === apartment.id;
                  
                  return (
                    <div
                      key={apartment.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-primary/10 border border-primary'
                          : isAssigned
                          ? 'bg-gray-100 opacity-50'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div>
                        <span className="font-medium">{apartment.number}</span>
                        <div className="text-sm text-gray-500">
                          {apartment.area} м² • {apartment.rooms} стаи
                        </div>
                      </div>
                      {(showApartmentForm || selectedShape) && !isAssigned && (
                        <button
                          onClick={() => handleAssignApartment(selectedShape || '', apartment.id)}
                          className="px-3 py-1 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
                        >
                          Избери
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleSave}
              disabled={floorPlans.length === 0 || isSaving}
              className="flex-1 bg-secondary text-white px-6 py-3 rounded-lg flex items-center justify-center hover:bg-secondary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  <span>Запазване...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  <span>Запази</span>
                </>
              )}
            </button>

            {floorPlans.length > 0 && (
              <button
                onClick={handleDelete}
                disabled={isSaving}
                className="bg-red-600 text-white px-6 py-3 rounded-lg flex items-center justify-center hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                <span>Изтрий</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorPlansEditor;